// Task #16: Global error notification system for confidence-based error handling
import React, { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, X, RefreshCw } from 'lucide-react';
import { useStore } from '../store/useStore';
import { ConfidenceError, getErrorDisplayInfo } from '../types';
import logger from '../utils/logger';

interface NotificationToast {
  id: string;
  error: ConfidenceError;
  displayInfo: ReturnType<typeof getErrorDisplayInfo>;
  timestamp: number;
  autoHide: boolean;
}

const ErrorNotifications: React.FC = () => {
  const {
    errorState,
    retryFailedOperation,
    removeConfidenceError,
    isRecoveringFromErrors,
  } = useStore();
  const [notifications, setNotifications] = useState<NotificationToast[]>([]);
  const [retryingErrors, setRetryingErrors] = useState<Set<string>>(new Set());

  // Convert new errors to notifications
  useEffect(() => {
    const currentNotificationIds = new Set(notifications.map((n) => n.id));
    const newErrors = errorState.errors.filter(
      (error) => !currentNotificationIds.has(error.id) && error.severity !== 'warning' // Only show error and critical notifications
    );

    if (newErrors.length > 0) {
      const newNotifications = newErrors.map((error) => ({
        id: error.id,
        error,
        displayInfo: getErrorDisplayInfo(error),
        timestamp: Date.now(),
        autoHide: error.severity === 'error', // Auto-hide error level, keep critical visible
      }));

      setNotifications((prev) => [...prev, ...newNotifications]);
    }
  }, [errorState.errors, notifications]);

  // Auto-hide notifications after delay
  useEffect(() => {
    const timer = setInterval(() => {
      setNotifications((prev) =>
        prev.filter(
          (notification) =>
            !notification.autoHide || Date.now() - notification.timestamp < 8000 // 8 second auto-hide
        )
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleRetryError = async (error: ConfidenceError) => {
    if (!error.recoverable || retryingErrors.has(error.id)) return;

    setRetryingErrors((prev) => new Set(prev).add(error.id));

    try {
      const success = await retryFailedOperation(error.id);
      if (success) {
        // Show success notification
        showSuccessNotification(
          `Successfully retried: ${error.errorType.replace(/_/g, ' ')}`
        );
        // Remove from notifications
        setNotifications((prev) => prev.filter((n) => n.id !== error.id));
      }
    } catch (retryError) {
      logger.error('Error during retry:', retryError);
    } finally {
      setRetryingErrors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(error.id);
        return newSet;
      });
    }
  };

  const handleDismissNotification = (notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    // Also remove from store if it's a dismissible error
    removeConfidenceError(notificationId);
  };

  const showSuccessNotification = (message: string) => {
    // This could be enhanced to show success notifications
    logger.debug('Success notification:', message);
  };

  const getNotificationIcon = (error: ConfidenceError) => {
    switch (error.severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationStyles = (error: ConfidenceError) => {
    switch (error.severity) {
      case 'critical':
        return 'bg-red-50 border-red-200 shadow-red-100';
      case 'error':
        return 'bg-orange-50 border-orange-200 shadow-orange-100';
      default:
        return 'bg-blue-50 border-blue-200 shadow-blue-100';
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {notifications.map((notification) => {
        const isRetrying = retryingErrors.has(notification.error.id);
        const canRetry =
          notification.error.recoverable && !isRetrying && !isRecoveringFromErrors;

        return (
          <div
            key={notification.id}
            className={`border rounded-lg shadow-lg ${getNotificationStyles(notification.error)} transition-all duration-300 ease-in-out transform translate-x-0`}
          >
            <div className="p-4">
              <div className="flex items-start space-x-3">
                {getNotificationIcon(notification.error)}

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm">
                    {notification.displayInfo.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {notification.displayInfo.message}
                  </p>

                  {/* Case context */}
                  {notification.error.caseNumber && (
                    <p className="text-xs text-gray-500 mt-1">
                      Case: {notification.error.caseNumber}
                    </p>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center space-x-3 mt-3">
                    {canRetry && (
                      <button
                        onClick={() => handleRetryError(notification.error)}
                        disabled={isRetrying}
                        className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw
                          className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`}
                        />
                        <span>{isRetrying ? 'Retrying...' : 'Retry'}</span>
                      </button>
                    )}

                    <button
                      onClick={() => handleDismissNotification(notification.id)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => handleDismissNotification(notification.id)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress bar for auto-hide */}
            {notification.autoHide && (
              <div className="h-1 bg-gray-200 rounded-b-lg overflow-hidden">
                <div
                  className="h-full bg-current opacity-30 transition-all duration-linear"
                  style={{
                    width: `${Math.max(0, 100 - ((Date.now() - notification.timestamp) / 8000) * 100)}%`,
                    transitionDuration: '1s',
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Global recovery indicator */}
      {isRecoveringFromErrors && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-4">
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
            <div>
              <h4 className="font-medium text-blue-900 text-sm">
                Recovering from errors...
              </h4>
              <p className="text-sm text-blue-700">
                Attempting to retry failed operations
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorNotifications;
